import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GoogleSignIn } from '@/components/auth/GoogleSignIn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Auth({ mode }) {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  useEffect(() => setIsLogin(mode === 'login'), [mode]);
  const handleAuthenticated = user => navigate(user.onboarding_complete ? '/dashboard' : '/onboarding', { replace: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (googleBusy || loading) return;
    
    setLoading(true);
    try {
      let user;
      if (isLogin) {
        user = await login(email, password);
      } else {
        user = await register(email, password, name, true, true);
      }
      
      handleAuthenticated(user);
    } catch (err) {
      if (!err.response) {
        toast.error('Unable to connect to server. Please check your connection and try again.');
      } else if (err.response.status === 401) {
        toast.error('Incorrect email or password');
      } else if (err.response.status === 404) {
        toast.error('Account not found. Check your email or sign up.');
      } else if (err.response.status === 429) {
        toast.error('Too many attempts. Please wait a moment and try again.');
      } else if (err.response.status >= 500) {
        toast.error('Server error. Please try again in a few minutes.');
      } else {
        toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="auth-page">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <img
          src="/auth-bg.jpg"
          alt="Training"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 p-8 text-white max-w-md lg:max-w-lg">
          <h2 className="text-2xl font-heading font-black uppercase tracking-tight mb-4 leading-[0.95]">
            Your fitness.<br />Your rules.
          </h2>
          <p className="text-white/60 text-lg">Personalized workouts. Adaptive coaching. Zero guesswork.</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col">
        <div className="p-6 flex items-center justify-between">
          <Button asChild variant="ghost" className="rounded-full gap-2" data-testid="auth-back-btn">
            <Link to="/" data-testid="auth-back-link"><ArrowLeft size={18} /> Back</Link>
          </Button>
        </div>

        <div className="flex-1 flex items-start sm:items-center justify-center p-5 overflow-y-auto">
          <motion.div
            className="w-full max-w-sm md:max-w-md lg:max-w-lg space-y-5 sm:space-y-8 py-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight mb-1 sm:mb-2 text-white" data-testid="auth-title">
                {isLogin ? 'Welcome back' : 'Create account'}
              </h1>
              <p className="text-base text-white/60 font-medium" data-testid="auth-subtitle">
                {isLogin ? 'Sign in to continue training' : 'Start your fitness journey today'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-5" data-testid="auth-email-form">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-heading font-bold text-white uppercase tracking-wider">Name</Label>
                  <Input
                    id="name" value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    className="h-12 sm:h-14 rounded-xl text-base"
                    required
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-heading font-bold text-white uppercase tracking-wider">Email</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 sm:h-14 rounded-xl text-base"
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-base font-heading font-bold text-white uppercase tracking-wider">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="h-12 sm:h-14 rounded-xl pr-12 text-base"
                    required minLength={6}
                    data-testid="auth-password-input"
                  />
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-8 w-8"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="auth-toggle-password"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
                {isLogin && (
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-accent hover:underline" data-testid="auth-forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>
              <Button
                type="submit" disabled={loading || googleBusy}
                className="w-full h-12 sm:h-14 bg-accent text-accent-foreground hover:bg-primary/90 rounded-xl font-heading font-bold uppercase tracking-wider text-base"
                data-testid="auth-submit-btn"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            {process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === 'true' && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-xs text-white/40" aria-hidden="true"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
                <GoogleSignIn onSuccess={handleAuthenticated} disabled={loading} onBusyChange={setGoogleBusy} />
              </div>
            )}
            <p className="text-sm text-white/75 leading-relaxed text-center" data-testid="auth-terms-notice">
              By creating an account, including with Google, you confirm you are at least 16 and agree to our{' '}
              <Link to="/legal/terms" className="underline hover:text-accent" target="_blank" rel="noopener noreferrer" data-testid="auth-consent-terms-link">Terms of Service</Link>{' '}and{' '}
              <Link to="/legal/privacy" className="underline hover:text-accent" target="_blank" rel="noopener noreferrer" data-testid="auth-consent-privacy-link">Privacy Policy</Link>.
            </p>

            <p className="text-center text-base text-white/60 font-medium" data-testid="auth-mode-prompt">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button" disabled={loading || googleBusy}
                onClick={() => { setIsLogin(!isLogin); navigate(isLogin ? '/register' : '/login'); }}
                className="text-accent hover:underline font-heading font-bold uppercase"
                data-testid="auth-toggle-mode"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
            
            {/* Legal Footer */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-center gap-4 text-sm text-white/40 font-medium">
                <Link to="/legal/privacy" className="hover:text-accent transition-colors" data-testid="auth-privacy-link">
                  Privacy Policy
                </Link>
                <span>•</span>
                <Link to="/legal/terms" className="hover:text-accent transition-colors" data-testid="auth-terms-link">
                  Terms of Service
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
